/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { TextStyle, Link, Icon } from '@shopify/polaris';
import { SettingsMajor, ChevronRightMinor } from '@shopify/polaris-icons';
import ReactTooltip from 'react-tooltip';

import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import ColorPickerItem from './color-picker-item';
import ColorPalette from './color-palette';
import { ELEMENTS, MENU_ITEM_VALUE } from 'src/constants/theme-settings.js';
import { URL } from 'src/constants/links';
import { checkRatioContrast } from 'src/helpers/color-helper';
import { updateAdvancedColorRespective } from 'src/helpers/shopify-integration-settings-helper';
import { ReactComponent as IconInfoWarning } from 'src/assets/images/shopify-integration-settings/info-warning.svg';
import { ReactComponent as RefreshIcon } from 'src/assets/images/common/refresh.svg';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class ColorForm extends Component<ComposedProps> {
  componentWillReceiveProps(nextProps) {
    const { formValues } = this.props;
    const { formValues: nextFormValues, updateField } = nextProps;

    const colors = _.get(formValues, `colors`);
    const nextColors = _.get(nextFormValues, `colors`);

    if (!_.isEqual(colors, nextColors)) {
      const { advancedColors, setting, isChangedPagination, paginationSetting } =
        updateAdvancedColorRespective(formValues, nextFormValues, this.isReset);

      this.isReset = false;
      updateField(`colors.${ELEMENTS.ADVANCED_COLOR}`, advancedColors);
      updateField(`setting`, setting);
      if (isChangedPagination) {
        updateField('pagination', paginationSetting);
      }
    }
  }

  handleGoToAdvancedColor = () => {
    this.props.updateFieldReducer('activeMenuItem', MENU_ITEM_VALUE.ADVANCED_COLOR);
  };

  handleResetColor = () => {
    const { themeSettingDefaultValue, updateField } = this.props;
    this.isReset = true;
    updateField('colors', themeSettingDefaultValue?.colors);
  };

  render() {
    const { i18n, formValues, updateField } = this.props;
    const primaryColor = _.get(formValues, `colors.${ELEMENTS.COLOR_BASE_1}`);
    const secondaryColor = _.get(formValues, `colors.${ELEMENTS.COLOR_BASE_2}`);
    const accentColor1 = _.get(formValues, `colors.${ELEMENTS.ACCENT_BASE_1}`);
    const accentColor2 = _.get(formValues, `colors.${ELEMENTS.ACCENT_BASE_2}`);
    const accentColor3 = _.get(formValues, `colors.${ELEMENTS.ACCENT_BASE_3}`);
    const accentColor4 = _.get(formValues, `colors.${ELEMENTS.ACCENT_BASE_4}`);

    const isValidContrast = checkRatioContrast(primaryColor, secondaryColor);

    return (
      <>
        <div className="color-form">
          <div style={{ paddingTop: '16px' }}>
            <span className="form-link">
              <TextStyle variation="subdued">
                {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.colors_UserGuide')}
              </TextStyle>{' '}
              <Link url={URL['ShopifyIntegrationSettingsColor']} external>
                {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Common.learnMore')}
                <span style={{ color: '#2C6ECB' }}>.</span>
              </Link>
            </span>
          </div>

          <div className="form-section" style={{ marginTop: '22px' }}>
            <div className="form-section__item form-section__item--standard-spacing">
              <div className="form-section__item__title-wrapper" style={{ marginBottom: '14px' }}>
                <p className="title" style={{ fontWeight: 600, marginBottom: 0 }}>
                  {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.colors_Base')}
                </p>

                <div
                  data-for="color-contrast"
                  data-tip="warning"
                  style={{
                    display: !isValidContrast ? 'block' : 'none'
                  }}>
                  <IconInfoWarning />
                </div>

                <ReactTooltip
                  id="color-contrast"
                  place="right"
                  effect="solid"
                  type="light"
                  delayHide={200}
                  // delayShow={200}
                  delayUpdate={200}
                  getContent={dataTip => {
                    return (
                      <span>
                        <TextStyle>
                          {i18n.translate(
                            'AdminHTML.ShopifyIntegrationSettings.Form.colors_NoteContrast'
                          )}
                        </TextStyle>{' '}
                        <Link url={URL['ShopifyIntegrationSettingsColorPaletteGuide']} external>
                          {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Common.learnMore')}
                        </Link>
                      </span>
                    );
                  }}
                />
              </div>

              <div className="color-picker-container">
                <ColorPickerItem
                  colorKey={`colors.${ELEMENTS.COLOR_BASE_1}`}
                  formValues={formValues}
                  updateField={updateField}
                  dataFor="color-form"
                  dataTip={i18n.translate(
                    `AdminHTML.ShopifyIntegrationSettings.Form.colors_Tooltip_EditColor`
                  )}
                  disableAlpha
                />
                <ColorPickerItem
                  colorKey={`colors.${ELEMENTS.COLOR_BASE_2}`}
                  formValues={formValues}
                  updateField={updateField}
                  dataFor="color-form"
                  dataTip={i18n.translate(
                    `AdminHTML.ShopifyIntegrationSettings.Form.colors_Tooltip_EditColor`
                  )}
                  disableAlpha
                />
              </div>

              <ColorPalette formValues={formValues} updateField={updateField} />
            </div>

            <div className="form-section__item form-section__item--standard-spacing form-section__item--bordered-top">
              <p className="title" style={{ fontWeight: 600, marginBottom: '14px' }}>
                {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.colors_Accent')}
              </p>

              <div className="color-picker-container">
                <ColorPickerItem
                  colorKey={`colors.${ELEMENTS.ACCENT_BASE_1}`}
                  formValues={formValues}
                  updateField={updateField}
                  dataFor="color-form"
                  dataTip={i18n.translate(
                    `AdminHTML.ShopifyIntegrationSettings.Form.colors_Tooltip_${
                      accentColor1 ? 'EditColor' : 'AddColor'
                    }`
                  )}
                />
                <ColorPickerItem
                  colorKey={`colors.${ELEMENTS.ACCENT_BASE_2}`}
                  formValues={formValues}
                  updateField={updateField}
                  dataFor="color-form"
                  dataTip={i18n.translate(
                    `AdminHTML.ShopifyIntegrationSettings.Form.colors_Tooltip_${
                      accentColor2 ? 'EditColor' : 'AddColor'
                    }`
                  )}
                />
              </div>

              <div className="color-picker-container" style={{ marginTop: '10px' }}>
                <ColorPickerItem
                  colorKey={`colors.${ELEMENTS.ACCENT_BASE_3}`}
                  formValues={formValues}
                  updateField={updateField}
                  dataFor="color-form"
                  dataTip={i18n.translate(
                    `AdminHTML.ShopifyIntegrationSettings.Form.colors_Tooltip_${
                      accentColor3 ? 'EditColor' : 'AddColor'
                    }`
                  )}
                />
                <ColorPickerItem
                  colorKey={`colors.${ELEMENTS.ACCENT_BASE_4}`}
                  formValues={formValues}
                  updateField={updateField}
                  dataFor="color-form"
                  dataTip={i18n.translate(
                    `AdminHTML.ShopifyIntegrationSettings.Form.colors_Tooltip_${
                      accentColor4 ? 'EditColor' : 'AddColor'
                    }`
                  )}
                />
              </div>
            </div>
            <ReactTooltip
              id="color-form"
              place="top"
              effect="solid"
              type="light"
              offset={{ top: -5 }}
            />
          </div>

          <div className="form-section form-section--bordered" style={{ marginTop: '28px' }}>
            <div className="menu-container">
              <div className="menu-container__item" onClick={this.handleGoToAdvancedColor}>
                <div className="menu-container__item__name">
                  <div className="advanced-color">
                    <Icon source={SettingsMajor} color="subdued" />
                  </div>

                  <span>
                    {i18n.translate(
                      'AdminHTML.ShopifyIntegrationSettings.MenuSettings.advancedSettings'
                    )}
                  </span>
                </div>
                <Icon source={ChevronRightMinor} />
              </div>
            </div>
          </div>
        </div>
        <div className="btn-reset" onClick={this.handleResetColor}>
          <RefreshIcon />{' '}
          <span> {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.resetColor')}</span>
        </div>
      </>
    );
  }
}

export default withI18n()(ColorForm);
